import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ModalService } from '../../../services/modal.service';
import { AppState } from '../../app.service';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MdnPipe } from '../../pipes/mdn.pipe';
import { ToastHelperService } from '../../../services/toast-helper.service';
import { RestrictNumbersDirective } from '../../directives/restrict-numbers.directive';
import { AccountService } from '../../../services/account.service';
import { SmsVerificationService } from '../../../services/sms-verification.service';

@Component({
  selector: 'app-add-activated-number-modal',
  imports: [CommonModule, TranslateModule, ReactiveFormsModule, MdnPipe, RestrictNumbersDirective],
  templateUrl: './add-activated-number-modal.component.html',
  styleUrl: './add-activated-number-modal.component.scss'
})
export class AddActivatedNumberModalComponent implements OnInit, OnDestroy {
  private dialog = inject(MatDialogRef<any>);
  private translate = inject(TranslateService);
  private appState = inject(AppState);
  private modalHelper = inject(ModalService);
  private formBuilder = inject(FormBuilder);
  private toastHelper = inject(ToastHelperService);
  private smsVerificationService = inject(SmsVerificationService);
  private accountService = inject(AccountService);

  public data = inject(MAT_DIALOG_DATA);
  public activatingPhoneForm: FormGroup;
  public verificationForm: FormGroup;
  public waitingConfirmCode = false;
  public successResult = false;
  public failureResult = false;
  public customFailureResult = false;
  public phoneNumber;

  ngOnInit(): void {
    this.activatingPhoneForm = this.formBuilder.group({
      phoneNumber: ['', Validators.compose([Validators.required, Validators.minLength(10), Validators.maxLength(10)])],
    });
    this.verificationForm = this.formBuilder.group({
      verificationCode: ['', Validators.compose([Validators.required, Validators.minLength(4)])],
    });
    document.body.classList.add('modal-open');
    const appRoot = document.querySelector('app-root');
    if (appRoot) {
      appRoot.removeAttribute('aria-hidden');
    }
  }

  ngOnDestroy() {
    document.body.classList.remove('modal-open');
    const appRoot = document.querySelector('app-root');
    if (appRoot) {
      appRoot.setAttribute('aria-hidden', 'true');
    }
  }

  beforeClose(): boolean {
    if (document.body.classList.contains('modal-open')) {
      document.body.classList.remove('modal-open');
    }
    return false;
  }

  closeDialog(res?: any): void {
    this.beforeClose();
    this.dialog.close(res);
  }

  sendCode(): void {
    this.activatingPhoneForm.markAllAsTouched();
    if (!this.activatingPhoneForm?.valid) {
      return;
    }

    this.appState.loading = true;
    this.callIsValidPhoneNumber();
  }

  callIsValidPhoneNumber(): void {
    this.phoneNumber = this.activatingPhoneForm.controls['phoneNumber'].value;
    this.accountService.isPhoneNumberExists(this.phoneNumber).then(() => {
      this.sendVerificationCode();
    }, (error) => {
      this.appState.loading = false;
      this.toastHelper.error(error?.error?.errors[0]?.message);
    });
  }

  verifyLoginCode(): void {
    this.verificationForm.markAllAsTouched();
    if (!this.verificationForm?.valid || !this.waitingConfirmCode) {
      return;
    }
    this.appState.loading = true;
    const code = this.verificationForm?.controls['verificationCode'].value;
    this.smsVerificationService.validateVerificationCode(this.phoneNumber, code).then(() => {
      this.appState.loading = false;
      this.successResult = true;
      this.failureResult = false;
      this.customFailureResult = false;
      this.waitingConfirmCode = false;
      this.done();
    }).catch((error) => {
      this.appState.loading = false;
      this.successResult = false;
      this.failureResult = true;
      this.customFailureResult = error?.error?.errors[0]?.message?.includes('Invalid temporary PIN');
    });
  }

  done(): void {
    if (!!this.phoneNumber) {
      this.appState.loading = true;
      this.accountService.addActivatedMdn(this.phoneNumber).then(() => {
        this.accountService.getUserAccount(this.phoneNumber).then(() => {
          // Refresh MDN list so account summary picks up the newly added number
          this.accountService.getUserMdns().catch(() => { /* ignore refresh errors here */ });
          this.appState.loading = false;
          this.dialog.close({ addedMdn: this.phoneNumber });
          const customHTML = `
          <p class="subnote">${this.translate.instant('activated-mdn.your-phone-number')}<b>${(new MdnPipe()).transform(this.phoneNumber)}</b> ${this.translate.instant('activated-mdn.successfully-added')}</p>
          <p class="smallest end-note">${this.translate.instant('activated-mdn.see-details')}</p>
                   `
          this.modalHelper.successModal({ customHTML });
        }, (error) => {
          this.appState.loading = false;
          this.dialog.close();
          this.modalHelper.failureModal({ msg: this.translate.instant('failure-modal.we-could-not-send-data'), buttonText: this.translate.instant('activated-mdn.got-it') });
        });
      }, (error) => {
        this.appState.loading = false;
        this.dialog.close();
        if (error?.error?.errors[0]?.textCode === 'MDN_ALREADY_EXIST') {
          this.modalHelper.failureModal({ msg: this.translate.instant('activated-mdn.phone-number-already-added'), buttonText: this.translate.instant('activated-mdn.got-it') });
        } else {
          this.modalHelper.failureModal({ msg: this.translate.instant('activated-mdn.we-couldnot-process-request'), buttonText: this.translate.instant('activated-mdn.got-it') });
        }
      });
    }
  }

  sendVerificationCode(): void {
    this.appState.loading = true;
    this.smsVerificationService.sendVerificationCode(this.phoneNumber).then(() => {
      this.waitingConfirmCode = true;
      this.appState.loading = false;
    }).catch((error) => {
      this.appState.loading = false;
      this.toastHelper.error(error?.error?.errors[0]?.message);
    });
  }


  cancel(): void {
    this.closeDialog();
  }

  recheckIntries(): void {
    this.customFailureResult = false;
    this.successResult = false;
    this.failureResult = false;
    this.verificationForm.reset();
    this.waitingConfirmCode = false;
  }
}
